import { useEffect, useRef, useState } from 'react'
import { Spin } from 'antd'
import { Navigate, useLocation } from 'react-router-dom'
import { refreshSessionFromGuard } from '../lib/httpClient'
import { isTokenExpired, useAuthSession } from '../lib/session'

function AuthRefreshFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spin size="large" />
    </div>
  )
}

export default function RequireAuth({ children }: { children: JSX.Element }) {
  const location = useLocation()
  const session = useAuthSession()
  const from = `${location.pathname}${location.search}${location.hash}`
  const tokenExpired = isTokenExpired(session)
  const [refreshState, setRefreshState] = useState<'idle' | 'refreshing' | 'failed'>('idle')
  const refreshStateRef = useRef(refreshState)

  useEffect(() => {
    refreshStateRef.current = refreshState
  }, [refreshState])

  useEffect(() => {
    if (!session || (!session.idToken && !session.refreshToken)) return
    if (!tokenExpired) {
      if (refreshStateRef.current !== 'idle') setRefreshState('idle')
      return
    }
    if (refreshStateRef.current !== 'idle') return

    let cancelled = false
    setRefreshState('refreshing')
    refreshSessionFromGuard()
      .then((ok) => {
        if (cancelled) return
        setRefreshState(ok ? 'idle' : 'failed')
      })
      .catch(() => {
        if (cancelled) return
        setRefreshState('failed')
      })

    return () => {
      cancelled = true
    }
  }, [session, tokenExpired])

  if (!session || (!session.idToken && !session.refreshToken)) {
    return <Navigate to="/login" state={{ from }} replace />
  }

  if (tokenExpired) {
    if (refreshState === 'refreshing' || refreshState === 'idle') return <AuthRefreshFallback />
    return <Navigate to="/login" state={{ from }} replace />
  }

  return children
}
